import { DfyTemplateType } from "@repo/api/dfy";
import { Input } from "@/lib/components/Input";
import { useDfyOrderStore } from "@/lib/stores/dfy-order-store/dfy-order-store";
import OptionCard from "../components/OptionCard";
import BrandingImageField from "../components/BrandingImageField";
import { DfyStepProps } from "../dfy-order-form-steps";
import DfyStepHeader from "../components/DfyStepHeader";
import DfyStepNavigation from "../components/DfyStepNavigation";
import { DFY_TEMPLATE_CONTENT } from "../dfy-order-form-content";

function FieldLabel({ children }: { children: React.ReactNode }) {
  return <span className="text-sm font-medium">{children}</span>;
}

export default function BrandingStep({ onBack, onNext }: DfyStepProps) {
  const details = useDfyOrderStore((state) => state.order?.details);
  const assetMedias = useDfyOrderStore((state) => state.assetMedias);
  const service = useDfyOrderStore((state) => state.service);

  if (!details) {
    return null;
  }

  const { templates, propertyDetails, agentDetails } = details;
  const logoExternalId = details.logoAssetMediaExternalId;

  const logoSrc = logoExternalId
    ? (assetMedias[logoExternalId]?.src ?? null)
    : null;
  const headshotExternalId = agentDetails.headshotAssetMediaExternalId;
  const headshotSrc = headshotExternalId
    ? (assetMedias[headshotExternalId]?.src ?? null)
    : null;

  function renderTemplateFields(type: DfyTemplateType) {
    switch (type) {
      case "PROPERTY_INTRO":
        return (
          <>
            <div className="flex min-w-0 flex-col gap-2">
              <FieldLabel>Property address</FieldLabel>
              <Input
                value={propertyDetails.address}
                onChange={(event) =>
                  service.updateDetails((draft) => {
                    draft.propertyDetails.address = event.target.value;
                  })
                }
              />
            </div>
            <div className="flex min-w-0 flex-col gap-2">
              <FieldLabel>Highlights</FieldLabel>
              <textarea
                className="min-h-24 w-full rounded-md border border-input bg-background px-3 py-2 text-sm outline-none focus:border-tertiary-400 focus:ring-1 focus:ring-tertiary-400"
                value={propertyDetails.highlights}
                onChange={(event) =>
                  service.updateDetails((draft) => {
                    draft.propertyDetails.highlights = event.target.value;
                  })
                }
              />
            </div>
          </>
        );
      case "AGENT_OUTRO":
        return (
          <>
            <div className="grid gap-4 sm:grid-cols-3">
              <div className="flex min-w-0 flex-col gap-2">
                <FieldLabel>Agent name</FieldLabel>
                <Input
                  value={agentDetails.name}
                  onChange={(event) =>
                    service.updateDetails((draft) => {
                      draft.agentDetails.name = event.target.value;
                    })
                  }
                />
              </div>
              <div className="flex min-w-0 flex-col gap-2">
                <FieldLabel>Title</FieldLabel>
                <Input
                  value={agentDetails.title}
                  onChange={(event) =>
                    service.updateDetails((draft) => {
                      draft.agentDetails.title = event.target.value;
                    })
                  }
                />
              </div>
              <div className="flex min-w-0 flex-col gap-2">
                <FieldLabel>Brokerage</FieldLabel>
                <Input
                  value={agentDetails.company}
                  onChange={(event) =>
                    service.updateDetails((draft) => {
                      draft.agentDetails.company = event.target.value;
                    })
                  }
                />
              </div>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="flex min-w-0 flex-col gap-2">
                <FieldLabel>Phone</FieldLabel>
                <Input
                  type="tel"
                  value={agentDetails.phone}
                  onChange={(event) =>
                    service.updateDetails((draft) => {
                      draft.agentDetails.phone = event.target.value;
                    })
                  }
                />
              </div>
              <div className="flex min-w-0 flex-col gap-2">
                <FieldLabel>Email</FieldLabel>
                <Input
                  type="email"
                  value={agentDetails.email}
                  onChange={(event) =>
                    service.updateDetails((draft) => {
                      draft.agentDetails.email = event.target.value;
                    })
                  }
                />
              </div>
            </div>
            <BrandingImageField
              label="Headshot"
              imageSrc={headshotSrc}
              fit="cover"
              onUpload={service.uploadHeadshot}
              onRemove={() =>
                service.updateDetails((draft) => {
                  draft.agentDetails.headshotAssetMediaExternalId = null;
                })
              }
            />
          </>
        );
      case "LOGO":
        return (
          <BrandingImageField
            label="Logo"
            imageSrc={logoSrc}
            fit="contain"
            onUpload={service.uploadLogo}
            onRemove={() =>
              service.updateDetails((draft) => {
                draft.logoAssetMediaExternalId = null;
              })
            }
          />
        );
    }
  }

  return (
    <>
      <DfyStepHeader
        title="Branding"
        description="Choose what to include, then add the details."
      />
      <div className="flex flex-col gap-8 py-6">
        <div className="flex flex-col gap-4">
          {DFY_TEMPLATE_CONTENT.map((option) => {
            const isSelected = templates.some(
              (item) => item.type === option.id,
            );
            return (
              <div key={option.id} className="flex flex-col">
                <OptionCard
                  option={option}
                  isSelected={isSelected}
                  control="checkbox"
                  onToggle={() => service.toggleTemplate(option.id)}
                />
                {isSelected && (
                  <div className="flex flex-col gap-5 pt-6 pb-10">
                    {renderTemplateFields(option.id)}
                  </div>
                )}
              </div>
            );
          })}
        </div>
        <DfyStepNavigation
          onBack={onBack}
          onNext={onNext}
          nextConfirmation={
            templates.length === 0
              ? {
                  title: "No branding selected",
                  description:
                    "You have not selected any branding to include in the video. Are you sure you want to continue?",
                }
              : null
          }
        />
      </div>
    </>
  );
}
